import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/lib/components/ui/dialog";
import { Button } from "@/lib/components/ui/button";
import { Input } from "@/lib/components/ui/input";
import { Label } from "@/lib/components/ui/label";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  unitPrice?: number | null;
  defaultQty?: number;
  busy?: boolean;
  onConfirm: (qty: number) => void;
};

export default function AddToCartDialog({
  open,
  onOpenChange,
  unitPrice,
  defaultQty = 1,
  busy = false,
  onConfirm,
}: Props) {
  const [qtyInput, setQtyInput] = React.useState<string>(defaultQty.toString());
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setQtyInput(defaultQty.toString());
      setError(null);
    }
  }, [open, defaultQty]);

  // Validate quantity input
  const validateQty = (value: string): string | null => {
    if (value.trim() === "") return "Please enter a quantity.";
    const num = parseInt(value, 10);
    if (isNaN(num) || num !== parseFloat(value) || num < 1) return "Quantity must be a positive integer.";
    return null;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQtyInput(value);
    // Clear error when user starts typing, but don't validate yet
    if (error && value.trim() !== "") {
      setError(null);
    }
  };

  const handleConfirm = () => {
    const validationError = validateQty(qtyInput);
    if (validationError) {
      setError(validationError);
      return;
    }
    const qty = parseInt(qtyInput, 10);
    onConfirm(qty);
  };

  const qty = parseInt(qtyInput || "0", 10);
  const total =
    unitPrice != null && isFinite(unitPrice) ? (unitPrice || 0) * qty : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby="add-to-cart-description">
        <DialogHeader>
          <DialogTitle>Add to cart</DialogTitle>
          <DialogDescription id="add-to-cart-description">
            Choose how many copies you want to print.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3">
          <div className="grid gap-2">
            <Label htmlFor="qty">How many copies?</Label>
            <div className="flex items-center gap-2">
              <div className="flex flex-col gap-1">
                <Input
                  id="qty"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={qtyInput}
                  onChange={handleInputChange}
                  className="w-28"
                  placeholder="1"
                />
                {error && <div className="text-sm text-red-500">{error}</div>}
              </div>
              <div className="ml-auto text-sm text-muted-foreground">
                {unitPrice != null && isFinite(unitPrice) ? (
                  <div className="text-right">
                    <div>Unit price: €{(unitPrice || 0).toFixed(2)}</div>
                    <div className="font-medium">
                      Total: €{(total ?? 0).toFixed(2)}
                    </div>
                  </div>
                ) : (
                  <div className="text-right">Calculating price…</div>
                )}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={busy || qtyInput.trim() === ""}
          >
            {busy ? "Adding…" : "Confirm"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}